/**
 * Resize a room that already has a layout on it.
 *
 * Each surface is stretched or cropped to its new size, and what is on it
 * follows one rule: an edge that sat on the surface's boundary stays on the
 * boundary; every other edge stays where it was, measured from the left and
 * from the floor. So a wall of boards still covers the wall, a SlatWall band
 * still starts at the same height, and a window keeps its place.
 *
 * Whatever no longer fits is cropped (sections), nudged back inside (windows
 * and doors) or, failing that, left out — the count comes back as `dropped`.
 *
 * Walls are in the viewer's box order [front, right, back, left]: front and
 * back are the room's length, right and left its width. The ceiling is
 * length × width.
 */

import { RoomDimensions } from '../../types';
import { Opening, RoomSurfaces, Section, Surface } from '../../types/sections';
import { SLATWALL_COVERAGE_HEIGHT } from '../constants';
import { intersectRect } from './resolver';
import { MIN_OPENING_SIZE_FT, MIN_SECTION_SIZE_FT, openingRect } from './validation';
import { fittingAccessories } from './accessories';

const EPS = 1e-6;
/** Kept between a nudged window or door and the wall's side (4"). */
const NUDGE_MARGIN_FT = 1 / 3;

export interface ResizeRoomResult {
  room: RoomSurfaces;
  /** Sections and openings that could not be kept. */
  dropped: number;
}

/** One edge pair along an axis: [start, end] on a span that went from `from` to `to`. */
function resizeSpan(start: number, size: number, from: number, to: number): [number, number] | null {
  const end = Math.abs(start + size - from) < EPS ? to : Math.min(start + size, to);
  const begin = Math.max(0, start);
  return end - begin < MIN_SECTION_SIZE_FT - EPS ? null : [begin, end - begin];
}

function resizeSection(section: Section, surface: Surface, width: number, height: number): Section | null {
  const across = resizeSpan(section.x, section.width, surface.width, width);
  const up = resizeSpan(section.y, section.height, surface.height, height);
  if (!across || !up) return null;
  let tall = up[1];
  if (section.productType === 'SlatWall') {
    // SlatWall comes in whole rows.
    tall = Math.floor(tall / SLATWALL_COVERAGE_HEIGHT + 1e-4) * SLATWALL_COVERAGE_HEIGHT;
    if (tall < SLATWALL_COVERAGE_HEIGHT - EPS) return null;
  }
  return { ...section, x: across[0], width: across[1], y: up[0], height: tall };
}

function resizeOpening(opening: Opening, width: number, height: number, kept: Opening[]): Opening | null {
  if (opening.width > width + EPS || opening.y + opening.height > height + EPS) return null;
  if (opening.width < MIN_OPENING_SIZE_FT - EPS || opening.height < MIN_OPENING_SIZE_FT - EPS) return null;
  let x = opening.x;
  if (x + opening.width > width + EPS) {
    const room = width - opening.width;
    x = Math.max(0, room - Math.min(NUDGE_MARGIN_FT, room / 2));
  }
  const next = { ...opening, x };
  return kept.some((other) => intersectRect(openingRect(next), openingRect(other))) ? null : next;
}

export function resizeSurface(surface: Surface, width: number, height: number): { surface: Surface; dropped: number } {
  let dropped = 0;
  const sections: Section[] = [];
  for (const section of surface.sections) {
    const next = resizeSection(section, surface, width, height);
    if (next) sections.push(next); else dropped++;
  }
  const next: Surface = { ...surface, width, height, sections };
  if (surface.openings) {
    const openings: Opening[] = [];
    for (const opening of surface.openings) {
      const moved = resizeOpening(opening, width, height, openings);
      if (moved) openings.push(moved); else dropped++;
    }
    next.openings = openings;
  }
  // A SlatWall section shrunk with the wall: the accessories it can no longer hold go too.
  next.sections = next.sections.map((section) => {
    if (!section.accessories) return section;
    const kept = fittingAccessories(next, section);
    dropped += (section.accessories.length - (kept?.length ?? 0));
    const out = { ...section };
    if (kept) out.accessories = kept; else delete out.accessories;
    return out;
  });
  return { surface: next, dropped };
}

export function resizeRoom(room: RoomSurfaces, to: RoomDimensions): ResizeRoomResult {
  let dropped = 0;
  const walls = room.walls.map((wall, i) => {
    const result = resizeSurface(wall, i % 2 === 0 ? to.length : to.width, to.height);
    dropped += result.dropped;
    return result.surface;
  });
  const next: RoomSurfaces = { ...room, walls };
  if (room.ceiling) {
    const result = resizeSurface(room.ceiling, to.length, to.width);
    dropped += result.dropped;
    next.ceiling = result.surface;
  }
  return { room: next, dropped };
}
